import { io, Socket as SocketIOSocket } from 'socket.io-client';

export interface ISocket {
    id?: string;
    connected: boolean;
    on(event: string, callback: (...args: any[]) => void): void;
    off(event: string, callback?: (...args: any[]) => void): void;
    emit(event: string, data?: any): void;
    disconnect(): void;
    connect(): void;
}

function normalizeSocketUrl(url: string): string {
    if (!url) return url;
    const trimmedUrl = url.trim();
    // Local dev usually runs Socket.IO over plain ws/http; wss://localhost fails without TLS cert setup.
    if (/^wss:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(trimmedUrl)) {
        return trimmedUrl.replace(/^wss:\/\//i, 'ws://');
    }
    return trimmedUrl;
}

export class SocketIOAdapter implements ISocket {
    private socket: SocketIOSocket;

    constructor(url: string, options?: any) {
        this.socket = io(url, options);
    }

    get id() { return this.socket.id; }
    get connected() { return this.socket.connected; }

    on(event: string, callback: (...args: any[]) => void): void {
        this.socket.on(event, callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        this.socket.off(event, callback);
    }

    emit(event: string, data?: any): void {
        this.socket.emit(event, data);
    }

    disconnect(): void {
        this.socket.disconnect();
    }

    connect(): void {
        this.socket.connect();
    }
}

// ── Tuning ───────────────────────────────────────────────────────────────────
// API Gateway WebSocket closes a connection after 10 MINUTES of no traffic in
// either direction, and hard-caps any connection at 2 hours. Ping well under
// the idle limit so a quiet inbox never gets dropped.
const PING_INTERVAL_MS = 4 * 60 * 1000;
// A socket can be readyState=OPEN while the underlying connection is already
// dead (network dropped without a close frame). Without a server reply we would
// never find out. If a ping goes unanswered, force-close and let reconnect run.
const PONG_TIMEOUT_MS = 10 * 1000;
// Client events that RELEASE a room. Each maps to the backend `unsubscribe`
// route and removes the room from the replay set. `leave_room` belongs here:
// conversation rooms must only deliver messages for the chat that is currently
// open, so closing/switching a chat has to actually drop the subscription.
const UNSUBSCRIBE_EVENTS = new Set([
    'leave_room',
    'unsubscribe',
    'unsubscribe-business-account',
    'unsubscribe-campaign',
]);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30 * 1000;
const RECONNECT_JITTER = 0.2;

/**
 * Adapter for AWS API Gateway WebSocket (raw `WebSocket`, not Socket.IO).
 *
 * Reconnection is OWNED HERE. `createSocket` cannot forward Socket.IO's
 * `reconnection` / `reconnectionAttempts` options to this class because they are
 * Socket.IO-specific — before this was implemented, a dropped connection was
 * never re-established and realtime silently died ~10 minutes after the last
 * message, until the user reloaded the page.
 *
 * Subscriptions are REPLAYED on every reopen. This is load-bearing: reconnecting
 * yields a NEW connectionId, `$disconnect` deletes the previous connection's rows
 * from the connections table, and `$connect` writes only the default "global"
 * row. A reconnect that does not re-subscribe is connected but receives nothing —
 * indistinguishable from the original bug.
 */
export class ServerlessWebSocketAdapter implements ISocket {
    private ws: WebSocket | null = null;
    private url: string;
    private eventListeners: Map<string, Set<Function>> = new Map();

    // Replayed on every (re)open — see class doc.
    private activeSubscriptions: Set<string> = new Set();
    private lastAuthPayload: any = null;

    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private pongTimer: ReturnType<typeof setTimeout> | null = null;
    /** Set by disconnect() so a deliberate close does not schedule a reconnect. */
    private intentionalClose = false;
    /** First open fires 'connect'; every later open fires 'reconnect'. */
    private hasConnectedOnce = false;

    public id?: string;
    public connected: boolean = false;

    constructor(url: string) {
        this.url = url;
        this.connect();
    }

    connect(): void {
        this.intentionalClose = false;
        this.clearReconnectTimer();

        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

        try {
            this.ws = new WebSocket(this.url);
        } catch (err) {
            // Constructor can throw on a malformed URL. Treat as a failed attempt
            // rather than an unhandled exception, so backoff still applies.
            this._trigger('connect_error', err);
            this.scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.connected = true;
            this.reconnectAttempts = 0;

            // Re-establish server-side state BEFORE announcing the connection, so
            // any handler that emits on 'connect'/'reconnect' lands after our
            // subscriptions rather than racing them.
            this.replaySessionState();
            this.startPing();

            if (this.hasConnectedOnce) {
                this._trigger('reconnect', this.reconnectAttempts);
            } else {
                this.hasConnectedOnce = true;
                this._trigger('connect', {});
            }
        };

        this.ws.onclose = () => {
            this.connected = false;
            this.stopPing();
            this._trigger('disconnect', 'closed');
            if (!this.intentionalClose) this.scheduleReconnect();
        };

        this.ws.onerror = (err) => {
            this._trigger('connect_error', err);
            // Do not reconnect here — `onerror` is always followed by `onclose`,
            // which owns scheduling. Reconnecting in both doubles the attempts.
        };

        this.ws.onmessage = (msg) => {
            try {
                const parsed = JSON.parse(msg.data);

                // Keepalive reply — consume it, never surface it to listeners.
                if (parsed.action === 'pong') {
                    this.clearPongTimer();
                    return;
                }

                // Protocol: { action: 'eventName', data: payload }, or a flat
                // { action: 'eventName', ...payload }.
                if (parsed.action && parsed.data) {
                    this._trigger(parsed.action, parsed.data);
                } else if (parsed.action) {
                    this._trigger(parsed.action, parsed);
                }
            } catch (e) {
                // Non-JSON frame — ignore.
            }
        };
    }

    // ── Reconnection ─────────────────────────────────────────────────────────

    private scheduleReconnect(): void {
        if (this.intentionalClose || this.reconnectTimer) return;

        // Exponential backoff with jitter. Jitter matters: without it, every
        // client dropped by the same API Gateway event retries in lockstep.
        const exponential = Math.min(
            RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
            RECONNECT_MAX_MS,
        );
        const jitter = exponential * RECONNECT_JITTER * (Math.random() * 2 - 1);
        const delay = Math.max(RECONNECT_BASE_MS, Math.round(exponential + jitter));

        this.reconnectAttempts += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Re-send everything the server forgot when the old connection died.
     * Sends directly rather than via emit() so a mid-replay failure cannot
     * mutate activeSubscriptions while we are iterating it.
     */
    private replaySessionState(): void {
        if (this.lastAuthPayload) {
            this.sendRaw({ action: 'authenticate', ...this.lastAuthPayload });
        }
        for (const roomId of this.activeSubscriptions) {
            this.sendRaw({ action: 'subscribe', roomId });
        }
    }

    // ── Keepalive ────────────────────────────────────────────────────────────

    private startPing(): void {
        this.stopPing();
        // Background tabs throttle timers, so this can fire late and the
        // connection may be dropped by API Gateway first. That is fine — onclose
        // schedules a reconnect, so it self-heals.
        this.pingTimer = setInterval(() => {
            if (!this.sendRaw({ action: 'ping' })) return;
            this.clearPongTimer();
            this.pongTimer = setTimeout(() => {
                // No reply: the connection is half-open. Force it closed so
                // onclose runs and reconnect takes over.
                this.pongTimer = null;
                try { this.ws?.close(); } catch { /* already gone */ }
            }, PONG_TIMEOUT_MS);
        }, PING_INTERVAL_MS);
    }

    private stopPing(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        this.clearPongTimer();
    }

    private clearPongTimer(): void {
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
    }

    // ── Listeners ────────────────────────────────────────────────────────────

    private _trigger(event: string, ...args: any[]) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            // Copy before iterating: a handler may off() itself.
            Array.from(listeners).forEach(cb => {
                try { cb(...args); } catch (err) { console.error(`[Socket] listener for "${event}" threw:`, err); }
            });
        }
    }

    on(event: string, callback: (...args: any[]) => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)!.add(callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        if (!callback) {
            this.eventListeners.delete(event);
        } else {
            this.eventListeners.get(event)?.delete(callback);
        }
    }

    // ── Sending ──────────────────────────────────────────────────────────────

    /** Raw send. Returns false when the socket is not writable. */
    private sendRaw(payload: Record<string, any>): boolean {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
        try {
            this.ws.send(JSON.stringify(payload));
            return true;
        } catch (err) {
            // Connection died between the readyState check and send().
            this._trigger('connect_error', err);
            try { this.ws.close(); } catch { /* already gone */ }
            return false;
        }
    }

    emit(event: string, data?: any): void {
        // Record session state FIRST, so a subscribe issued while offline is
        // replayed on the next open. This replaces the old `pendingEmits` buffer,
        // which only captured emits made while disconnected and therefore lost
        // every subscription that had previously succeeded.
        if (event === 'authenticate') {
            this.lastAuthPayload = data ?? {};
        }

        const roomId = this.roomIdFor(event, data);

        if (UNSUBSCRIBE_EVENTS.has(event)) {
            if (roomId) {
                // Drop it locally FIRST so a replay after reconnect cannot resurrect
                // it, then tell the server. Order matters: if the send fails because
                // the socket is down, the room is already gone from the replay set
                // and the new connection simply never subscribes to it.
                this.activeSubscriptions.delete(roomId);
                this.sendRaw({ action: 'unsubscribe', roomId });
            }
            return;
        }

        if (roomId) {
            this.activeSubscriptions.add(roomId);
            this.sendRaw({ action: 'subscribe', roomId });
            return;
        }

        // Anything else goes through as-is; unknown routes fall to $default.
        this.sendRaw({ action: event, ...data });
    }

    /**
     * Map a client-side event to the backend's room id, or null when the event
     * is not room-related. Single source of truth so emit() and the unsubscribe
     * branch cannot drift apart.
     */
    private roomIdFor(event: string, data?: any): string | null {
        // NOTE on 'leave_room'/'unsubscribe': these MUST resolve a room id here,
        // otherwise emit() falls through to the generic
        // `sendRaw({action:'leave_room'})`, which hits API Gateway's $default route
        // and does nothing. That was the subscription leak — a conversation room,
        // once joined, was never released, so the user kept receiving every chat
        // they had ever opened and the reconnect replay from #1434 faithfully
        // re-subscribed the whole leaked set.
        switch (event) {
            case 'join_room':
            case 'subscribe':
            case 'leave_room':
            case 'unsubscribe':
                return (typeof data === 'string' ? data : data?.roomId) ?? null;
            case 'subscribe-business-account':
            case 'unsubscribe-business-account': {
                const id = typeof data === 'string' ? data : data?.businessAccountId ?? data;
                return id ? `business-account-${id}` : null;
            }
            case 'subscribe-campaign':
            case 'unsubscribe-campaign': {
                const id = typeof data === 'string' ? data : data?.campaignId ?? data;
                return id ? `campaign-${id}` : null;
            }
            default:
                return null;
        }
    }

    disconnect(): void {
        this.intentionalClose = true;
        this.clearReconnectTimer();
        this.stopPing();
        this.activeSubscriptions.clear();
        this.lastAuthPayload = null;
        try { this.ws?.close(); } catch { /* already gone */ }
    }
}

export function createSocket(url: string, options?: any): ISocket {
    const resolvedUrl = normalizeSocketUrl(url);

    if (resolvedUrl.startsWith('ws://') || resolvedUrl.startsWith('wss://')) {
        // AWS API Gateway WebSocket URLs look like wss://<id>.execute-api.<region>.amazonaws.com/<stage>
        if (resolvedUrl.includes('execute-api')) {
            // `options` is deliberately NOT forwarded: it carries Socket.IO-only
            // keys (reconnection, reconnectionAttempts, transports, path) that this
            // adapter cannot honour. Reconnection is implemented natively above.
            return new ServerlessWebSocketAdapter(resolvedUrl);
        }
    }
    return new SocketIOAdapter(resolvedUrl, options);
}
